import { useState, useEffect, useRef } from 'react';
import { picksAPI } from '../services/api';

/**
 * Custom hook to detect when round data cache has been invalidated
 * Polls the heartbeat endpoint to check cache TTL changes
 *
 * @param {number} currentRound - The current round number to monitor
 * @param {boolean} enabled - Whether the heartbeat should be active
 * @param {number} interval - Polling interval in milliseconds (default: 30000 = 30s)
 * @returns {Object} { shouldRefresh, resetRefreshFlag, pauseMonitoring }
 */
export const useRoundDataHeartbeat = (currentRound, enabled = true, interval = 30000) => {
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const lastScoresUpdatedAtRef = useRef(null);
  const isFirstCheckRef = useRef(true);
  const pausedUntilRef = useRef(null);

  useEffect(() => {
    // Reset when round changes
    lastScoresUpdatedAtRef.current = null;
    isFirstCheckRef.current = true;
    setShouldRefresh(false);
    pausedUntilRef.current = null;
  }, [currentRound]);

  useEffect(() => {
    if (!enabled || !currentRound) return;

    const checkCacheStatus = async () => {
      try {
        const response = await picksAPI.getRoundHeartbeat(currentRound);
        const { scoresUpdatedAt } = response.data;

        // Skip the first check - just establish baseline
        if (isFirstCheckRef.current) {
          lastScoresUpdatedAtRef.current = scoresUpdatedAt;
          isFirstCheckRef.current = false;
          return;
        }

        // Check if monitoring is paused (due to user action)
        const now = Date.now();
        if (pausedUntilRef.current && now < pausedUntilRef.current) {
          console.log('[Heartbeat] Monitoring paused, updating baseline without triggering refresh');
          lastScoresUpdatedAtRef.current = scoresUpdatedAt;
          return;
        } else if (pausedUntilRef.current && now >= pausedUntilRef.current) {
          console.log('[Heartbeat] Monitoring resumed');
          pausedUntilRef.current = null;
        }

        // Trigger refresh only when scores were explicitly updated
        if (scoresUpdatedAt !== lastScoresUpdatedAtRef.current) {
          console.log('[Heartbeat] Scores updated, triggering refresh');
          setShouldRefresh(true);
        }

        lastScoresUpdatedAtRef.current = scoresUpdatedAt;
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('[Heartbeat] Check failed:', error.message);
      }
    };

    // Initial check
    checkCacheStatus();

    // Set up polling interval
    const intervalId = setInterval(checkCacheStatus, interval);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [currentRound, enabled, interval]);

  const resetRefreshFlag = () => {
    setShouldRefresh(false);
  };

  /**
   * Pause monitoring for a specified duration (e.g., after user saves picks)
   * During pause, TTL baseline is updated but no refresh is triggered
   * @param {number} durationMs - Duration to pause in milliseconds (default: 60000 = 60s)
   */
  const pauseMonitoring = (durationMs = 60000) => {
    pausedUntilRef.current = Date.now() + durationMs;
    console.log(`[Heartbeat] Monitoring paused for ${durationMs / 1000}s`);
  };

  return {
    shouldRefresh,
    resetRefreshFlag,
    pauseMonitoring
  };
};
